/* ==========================================================================
   rn-config-loader.js

   Loads the configurator on demand instead of rendering it into the initial
   page HTML. Used by the Renegade, Helix, Ballista, Rebel and Pandora
   landing templates.

   NOTHING LOADS UNTIL SOMEBODY CLICKS.
   An earlier version pre-warmed the fetch once the visitor scrolled past the
   first screen. That was a mistake: rendering crawlers routinely auto-scroll
   or use a very tall viewport to trigger lazy-loaded content, so the
   component catalogue was being handed straight back to the crawler this
   exists to hide it from. The pre-warm is gone. The first click now costs
   about a second of loading, which is the correct trade.

   DEEP LINKS NEED A REAL GESTURE, NOT A URL.
   /pages/helix?cpu=amd-cpu-7000#configure is a crawlable link. Opening the
   configurator just because that URL was requested means a rendering crawler
   following the link gets the whole catalogue. So the click handler records
   intent in sessionStorage, and only an arrival carrying that intent opens
   the configurator automatically. Crawlers do not carry sessionStorage
   between requests, so they never trigger it; a person clicking through from
   another page does.

   Trade-off worth knowing: a ?cpu= link pasted fresh into the address bar or
   opened from an email lands on the editorial page rather than the
   configurator. The entry points are right there, one click away.

   ==========================================================================
   STAGING A PAGE UNDER A DIFFERENT HANDLE
   ==========================================================================

   The fragment URL is built from the current location, and singleChipsetConfig
   reassigns pageH from page.handle. Between them that means a staging copy of
   a landing page at, say, /pages/pandora-v2 fetches its configurator from
   /pages/pandora-v2?view=... and gets an empty one back, because no products
   are tagged Pandora-v2.

   An optional attribute on the mount point overrides where the fragment is
   fetched from:

       <div id="configure" data-rn-config
            data-rn-config-view="confonly"
            data-rn-config-source="/pages/pandora"></div>

   Absent or empty, which is the case on every live page, the behaviour is
   byte-for-byte what it was before: the fragment comes from the current URL.
   Set, it comes from the named path instead, and the configurator populates
   against that page's handle while the staging page renders around it.

   ONE THING THIS HAD TO GET RIGHT
   Theme previews are served with preview_theme_id on the query string.
   Building the fragment URL from window.location carried that across for
   free. An explicit source has to copy it over by hand, or the fragment
   comes back from the published theme while the page around it is the one
   being previewed — which would look like the override silently not working.
   fragmentUrl below does that copy.

   The failure fallback, fullPageUrl, deliberately still uses the real
   location. If the fetch dies, the visitor belongs on the page they are
   actually on, not on the source page.

   ==========================================================================
   HOW A PLATFORM CHOICE ACTUALLY REACHES THE CONFIGURATOR
   ==========================================================================

   Worth reading before changing anything here, because there are two routes
   and they are not interchangeable.

   Each Generation product carries a metafield holding the exact call the
   configurator needs, e.g.
       fetchProducts('amd-cpu-7000'),fetchMB('amd-7000-motherboards')
   attached as an inline onclick on a button. Those two functions populate
   the processor, memory and motherboard lists and nothing else — every other
   category is rendered by Liquid from the page tag. That is why a failure
   here loses exactly those three and leaves the rest looking healthy.

   ROUTE ONE — the gate, further down.
   Catches the click on the button itself, in the capture phase, before the
   inline onclick can run. Needed because that onclick would otherwise fire
   about a second before the configurator markup arrives and throw
   'fetchProducts is not defined'. This is the ONLY route on Pandora, whose
   Generation cards link to #configure with no cpu= for anything else to
   match on.

   ROUTE TWO — the sameDocument path in the link handler.
   Helix, Ballista and Rebel link to an absolute URL carrying ?cpu=...&mb=...
   The handler recognises it as this page, loads the configurator, then calls
   clickGeneration. This route is what those three pages actually depend on.

   Both routes are legitimate and both must stay.

   THE DOUBLE CALL  (fixed 09 Sept)
   On the three pages that have both routes, one click ran the generation
   call twice: the gate ran it, then clickGeneration synthesised a button
   click which, with the configurator now loaded, slipped past the gate's
   early return and ran the inline onclick again. Visible in the console as
   two singleChipsetConfig blocks per click.

   It was never customer-facing — the second call overwrote the first with
   identical data rather than appending, so lists and prices stayed correct —
   but it doubled the work on every platform choice and left two mechanisms
   silently propping each other up.

   The fix is not to remove a route. Both are needed, on different pages. Both
   now funnel through runGeneration, which drops a repeat of the call it just
   ran. Switching platform still works because the call string changes; the
   window only suppresses the same platform twice in quick succession, which
   is a no-op anyway.

   ONE BEHAVIOURAL CHANGE WORTH KNOWING
   The gate now stops propagation on every generation click, including after
   the configurator has loaded. That means the theme's jQuery .nav-linki
   handler never sees these buttons and never does its tab switching.
   reveal() below performs the same work by hand, and has been doing so for
   the first click on every one of these pages already.

   ==========================================================================

   FOUR THINGS THE FETCH HAS TO GET RIGHT, all found by auditing the snippet:

   1. Scripts inserted as HTML never execute. Each one is recreated as a
      fresh element. External scripts are awaited so order is preserved.

   2. The fragment loads jQuery 3.6.0 from code.jquery.com in the middle of
      its markup. Running that again would swap window.jQuery for a fresh
      instance and orphan every handler already bound, so it is skipped.

   3. Most configurator handlers are directly bound rather than delegated.
      They attach to whatever exists when the script runs, so the markup must
      be in the DOM first. It is.

   4. Running twice would double-bind handlers and double-count prices. The
      state below guarantees one execution, and concurrent callers share the
      same promise.
   ========================================================================== */

(function () {
  'use strict';

  var mount = document.getElementById('configure');
  if (!mount || !mount.hasAttribute('data-rn-config')) return;

  var VIEW = mount.getAttribute('data-rn-config-view') || 'confonly';
  var SOURCE = mount.getAttribute('data-rn-config-source') || '';
  var INTENT = 'rn-config-intent';
  var state = 'idle';   /* idle | loading | ready | failed */
  var pending = null;

  /* lp-preselect puts an lp-jump class on <html> the moment it sees a cpu
     parameter, which hides #overview and reveals #configure for up to eight
     seconds while it hunts for a Generation button. With the configurator no
     longer in the page that would blank the landing page for nothing, so the
     class comes straight back off. Deep links are this loader's job now. */
  document.documentElement.classList.remove('lp-jump');

  function store(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function read(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function clear(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  /* Built from the current URL so theme previews keep working: the
     preview_theme_id parameter has to survive, or the fetch would come back
     from the published theme instead of the one being previewed.

     data-rn-config-source overrides the base only. Everything else about the
     request is unchanged, and preview_theme_id is copied across explicitly
     because an overridden base does not inherit it. See the note at the top. */
  function fragmentUrl() {
    var here = new URL(window.location.href);
    var url = SOURCE ? new URL(SOURCE, window.location.origin) : here;

    if (SOURCE) {
      var preview = here.searchParams.get('preview_theme_id');
      if (preview) { url.searchParams.set('preview_theme_id', preview); }
    }

    url.searchParams.set('view', VIEW);
    url.searchParams.delete('cpu');
    url.searchParams.delete('mb');
    url.hash = '';
    return url.toString();
  }

  /* Always the real location, never the override. A failed fetch should land
     the visitor on the page they are on. */
  function fullPageUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete('view');
    return url.toString();
  }

  function setBusy(busy) {
    mount.setAttribute('aria-busy', busy ? 'true' : 'false');
    document.documentElement.classList.toggle('rn-config-loading', busy);
  }

  function isJquery(src) {
    return /jquery/i.test(src || '');
  }

  function runScripts(scripts) {
    return scripts.reduce(function (chain, original) {
      return chain.then(function () {
        var src = original.getAttribute('src');
        if (src && isJquery(src)) return;

        return new Promise(function (resolve) {
          var s = document.createElement('script');
          for (var i = 0; i < original.attributes.length; i++) {
            s.setAttribute(original.attributes[i].name, original.attributes[i].value);
          }
          if (src) {
            s.onload = resolve;
            s.onerror = resolve;  /* never stall the chain */
            document.body.appendChild(s);
          } else {
            s.text = original.textContent;
            document.body.appendChild(s);
            resolve();
          }
        });
      });
    }, Promise.resolve());
  }

  function load() {
    if (state === 'ready') return Promise.resolve();
    if (pending) return pending;

    state = 'loading';
    setBusy(true);

    pending = fetch(fragmentUrl(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Configurator fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fragment = doc.querySelector('[data-rn-config-fragment]') || doc.body;

        /* The snippet carries its own span#configure, which would be a second
           element with that id once injected. */
        var dupe = fragment.querySelector('#configure');
        if (dupe) { dupe.id = 'configure-inner'; }

        var scripts = Array.prototype.slice.call(fragment.querySelectorAll('script'));
        scripts.forEach(function (s) { s.parentNode.removeChild(s); });

        mount.appendChild(fragment);   /* markup first, scripts second */
        return runScripts(scripts);
      })
      .then(function () {
        state = 'ready';
        setBusy(false);
        mount.dispatchEvent(new CustomEvent('rn:config-ready', { bubbles: true }));
      })
      .catch(function (err) {
        state = 'failed';
        pending = null;   /* allow a retry on the next click */
        setBusy(false);
        if (window.console) { console.error('[rn-config]', err); }
        window.location.href = fullPageUrl();
      });

    return pending;
  }

  function reveal() {
    return load().then(function () {
      mount.classList.remove('hidden');
      var sidebar = document.querySelector('.float-sidebar');
      if (sidebar) { sidebar.classList.remove('hidden'); }
      var overview = document.getElementById('overview');
      if (overview) { overview.classList.add('hidden'); }
      window.scrollTo(0, 0);
    });
  }

  /* ---- the single place a generation call is executed ---------------------
     Both routes described at the top of this file end up here. A repeat of
     the call that just ran is dropped, which is what removes the double call
     without having to take away either route.

     The window is deliberately short. It only ever needs to cover the gap
     between the two routes firing off one click, which is a few milliseconds
     once reveal() settles. Anything longer would start swallowing a genuine
     re-click. */

  var lastCall = '';
  var lastCallAt = 0;
  var REPEAT_WINDOW = 1500;

  function runGeneration(call, context) {
    if (!call) return;

    var now = Date.now();
    if (call === lastCall && (now - lastCallAt) < REPEAT_WINDOW) return;

    lastCall = call;
    lastCallAt = now;

    try {
      /* Runs the metafield call verbatim so the data stays on the product
         where it lives, rather than being reproduced here. */
      new Function(call).call(context || document.body);
    } catch (err) {
      if (window.console) { console.error('[rn-config] generation call failed', err); }
    }
  }

  function generationCall(node) {
    if (!node || !node.getAttribute) return '';
    var handler = node.getAttribute('onclick') || '';
    return handler.indexOf('fetchProducts') !== -1 ? handler : '';
  }

  /* ---- route one: the gate ------------------------------------------------
     Capture phase, so this runs before the *-chipset snippets' delegated
     handlers and before the button's own inline onclick.

     Matched on the onclick containing fetchProducts rather than on a class or
     an id, because the same call reaches us from three different places: the
     visible Generation cards on MultiChipsetLanding, the hidden buttons in
     the *-chipset snippets, and clicks those snippets synthesise themselves.
     All three carry the metafield call; nothing else on the page does.

     There is deliberately no early return once loaded. Letting a click
     through natively after load is what produced the double call. */

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[onclick]') : null;
    var call = generationCall(button);
    if (!call) return;

    /* stopPropagation keeps the event from reaching the button, which is what
       prevents the inline onclick running before there is anything to fill. */
    event.preventDefault();
    event.stopPropagation();

    /* reveal() resolves immediately when already loaded and re-applies the
       panel visibility, so this is safe on the second and third click too. */
    reveal().then(function () { runGeneration(call, button); });
  }, true);

  /* ---- route two: links --------------------------------------------------
     Delegated, so it covers buttons that arrive later and anything the theme
     renders itself. Any link to #configure counts as a request for it. */

  document.addEventListener('click', function (event) {
    var link = event.target.closest
      ? event.target.closest('a[href*="#configure"], a[href*="cpu="], [data-rn-config-open]')
      : null;
    if (!link) return;

    var href = link.getAttribute('href') || '';
    var match = href.match(/[?&]cpu=([a-z0-9-]+)/i);

    /* If the link points at another page, record the intent and let the
       navigation happen — the loader on that page picks it up. */
    var sameDocument = href.indexOf('#') === 0 ||
                       href.indexOf(window.location.pathname) !== -1;
    if (!sameDocument) {
      if (match) { store(INTENT, match[1]); }
      return;
    }

    event.preventDefault();

    reveal().then(function () {
      if (!match) return;
      clickGeneration(match[1]);
    });
  });

  /* Runs the matching button's call directly rather than synthesising a
     click on it. A synthetic click would re-enter the gate and, once loaded,
     was how the second call used to get through. */
  function clickGeneration(handle) {
    var needle = String.fromCharCode(39) + handle + String.fromCharCode(39);
    var all = document.querySelectorAll('[onclick]');
    for (var i = 0; i < all.length; i++) {
      if ((all[i].getAttribute('onclick') || '').indexOf(needle) !== -1) {
        runGeneration(generationCall(all[i]), all[i]);
        return;
      }
    }
  }

  /* Arrival with a cpu parameter opens the configurator ONLY when the intent
     flag from a real click is present. See the note at the top of this file. */
  (function gatedArrival() {
    var params = new URLSearchParams(window.location.search);
    var cpu = params.get('cpu');
    if (!cpu) return;

    var intent = read(INTENT);
    if (!intent) return;   /* a crawler, a bookmark, a pasted link */

    clear(INTENT);
    reveal().then(function () { clickGeneration(cpu); });
  })();
})();
